/**
 * B.10 — Operator «График». 14-day × 17-hour availability grid with A17 zones
 * (nearest 7 days закреплено, rest editable). Tap cycles none→free→busy→none;
 * horizontal pointer-drag paints the row. Counters over the whole grid. Sticky
 * footer: ВСЁ СВОБОДНО / СБРОС (editable zone) + dirty ОТПРАВИТЬ → ✓ ОТПРАВЛЕНО.
 * Ported 1:1 from design-references/screen-10-op-schedule.html.
 */
import { useRef } from "react";
import { useAvailGrid, AVAIL_HOURS, AVAIL_UNLOCK_UNTIL, type AvailDay, type CellStatus } from "../../hooks/useAvailGrid";
import { WarnTri, Check } from "./icons";
import css from "./ScheduleTab.module.css";

export function ScheduleTab({ operatorId }: { operatorId?: string }) {
  const g = useAvailGrid(operatorId);

  /* pointer painting — decide axis after 8px, paint horizontally */
  const drag = useRef<{
    down: AvailDay | null;
    hour: number;
    x: number;
    y: number;
    decided: boolean;
    horizontal: boolean;
    paint: CellStatus | null;
    touched: Set<string>;
  }>({ down: null, hour: -1, x: 0, y: 0, decided: false, horizontal: false, paint: null, touched: new Set() });

  function cellEditable(d: AvailDay): boolean {
    return !d.locked && !d.weekend;
  }

  function onPointerDown(e: React.PointerEvent, d: AvailDay, hour: number) {
    if (!cellEditable(d)) return;
    drag.current = {
      down: d, hour, x: e.clientX, y: e.clientY,
      decided: false, horizontal: false, paint: null, touched: new Set(),
    };
  }

  function onPointerMove(e: React.PointerEvent) {
    const s = drag.current;
    if (!s.down) return;
    if (!s.decided) {
      const dx = Math.abs(e.clientX - s.x);
      const dy = Math.abs(e.clientY - s.y);
      if (dx <= 8 && dy <= 8) return;
      s.decided = true;
      if (dx > dy) {
        s.horizontal = true;
        g.cycleCell(s.down.key, s.hour); // first cell defines paint state
        const cur = g.grid[s.down.key]?.[s.hour] ?? "none";
        s.paint = nextOf(cur);
        s.touched.add(`${s.down.key}:${s.hour}`);
      } else {
        s.down = null; // vertical → let scroll happen
        return;
      }
    }
    if (s.horizontal && s.paint) {
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const cellEl = el?.closest<HTMLElement>("[data-key]");
      if (!cellEl) return;
      const key = cellEl.dataset.key!;
      const hour = Number(cellEl.dataset.hour);
      const id = `${key}:${hour}`;
      if (s.touched.has(id) || cellEl.dataset.editable !== "1") return;
      g.setCell(key, hour, s.paint);
      s.touched.add(id);
    }
  }

  function onPointerUp() {
    const s = drag.current;
    if (s.down && !s.decided) g.cycleCell(s.down.key, s.hour); // simple tap
    drag.current = { down: null, hour: -1, x: 0, y: 0, decided: false, horizontal: false, paint: null, touched: new Set() };
  }

  const editableDays = g.days.filter((d) => !d.locked);
  const lockedDays = g.days.filter((d) => d.locked);

  return (
    <div className={css.screen}>
      <div className={css.main}>
        <div className={css.warnRow}>
          <WarnTri />
          <span>{Date.now() > AVAIL_UNLOCK_UNTIL ? <>Открыты <b>дни 8–14</b>. Ближайшие 7 дней закреплены.</> : <>Временно открыты <b>ВСЕ дни</b> — заполни график (переход на новый юнибукинг).</>}</span>
        </div>

        <div className={css.secRow}>
          <span className={css.secTitle}>Доступность</span>
          <span className={css.counters}>
            <span className={`${css.c} ${css.free}`}><span className={css.dot} /><b>{g.counters.free}</b> свободно</span>
            <span className={`${css.c} ${css.busy}`}><span className={css.dot} /><b>{g.counters.busy}</b> занято</span>
            <span className={`${css.c} ${css.emptyC}`}><span className={css.dot} /><b>{g.counters.none}</b> пусто</span>
          </span>
        </div>

        <div className={css.hint}>тап = переключить · свайп вбок = рисовать</div>

        {g.error ? <div className={css.err}>{g.error}</div> : null}

        <div className={css.gridWrap}>
          <div
            className={css.grid}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div className={css.corner} />
            {AVAIL_HOURS.map((h) => (
              <div key={`hh-${h}`} className={`${css.hh} ${h < 10 ? css.night : ""}`}>
                {String(h).padStart(2, "0")}
              </div>
            ))}

            <ZoneRow locked label="Закреплено" sub="7 дней" />
            {lockedDays.map((d) => (
              <DayRow key={d.key} d={d} grid={g.grid} onDown={onPointerDown} editable={false} />
            ))}

            <ZoneRow locked={false} label="Можно менять" sub="7 дней" />
            {editableDays.map((d) => (
              <DayRow key={d.key} d={d} grid={g.grid} onDown={onPointerDown} editable={cellEditable(d)} />
            ))}
          </div>
        </div>
      </div>

      <div className={css.sheetFoot}>
        <div className={css.btnRow}>
          <button type="button" className={`${css.btn} ${css.allFree}`} onClick={() => g.fillEditable("free")}>
            Всё свободно
          </button>
          <button type="button" className={`${css.btn} ${css.reset}`} onClick={() => g.fillEditable("none")}>
            Сброс
          </button>
          <button
            type="button"
            className={`${css.btn} ${css.submit} ${g.sent ? css.sent : ""}`}
            onClick={g.save}
            disabled={g.saving}
          >
            {g.sent ? <Check /> : <WarnTri />}
            {g.sent ? "Отправлено" : g.saving ? "Отправка…" : "Отправить"}
          </button>
        </div>
      </div>
    </div>
  );
}

function nextOf(cur: CellStatus): CellStatus {
  return cur === "none" ? "free" : cur === "free" ? "busy" : "none";
}

function ZoneRow({ locked, label, sub }: { locked: boolean; label: string; sub: string }) {
  return (
    <div className={`${css.zoneRow} ${locked ? css.zLocked : css.zEditable}`}>
      <span>{label}</span>
      <span className={css.zSub}>{sub}</span>
    </div>
  );
}

function DayRow({
  d, grid, onDown, editable,
}: {
  d: AvailDay;
  grid: Record<string, Record<number, CellStatus>>;
  onDown: (e: React.PointerEvent, d: AvailDay, hour: number) => void;
  editable: boolean;
}) {
  const row = grid[d.key] ?? {};
  const dlCls = [
    css.dl,
    d.today ? css.today : "",
    d.calWeekend ? css.we : "",
    d.locked ? css.locked : "",
    d.weekend ? css.weekend : "",
  ].filter(Boolean).join(" ");

  return (
    <>
      <div className={dlCls}>
        <span className={css.dw}>{d.dowLabel}</span>
        <span className={css.dd}>{d.dayNum}</span>
        {d.weekend ? <span className={css.weTag}>ВЫХ</span> : null}
      </div>
      {AVAIL_HOURS.map((h) => {
        const st = row[h] ?? "none";
        const cls = [
          css.cell,
          st === "free" ? css.cFree : st === "busy" ? css.cBusy : "",
          d.locked ? css.cLocked : "",
          d.weekend ? css.cWeekend : "",
          h < 10 ? css.cNight : "",
          h === 0 ? css.cDivider : "",
        ].filter(Boolean).join(" ");
        return (
          <div
            key={`${d.key}-${h}`}
            className={cls}
            data-key={d.key}
            data-hour={h}
            data-editable={editable ? "1" : "0"}
            onPointerDown={(e) => onDown(e, d, h)}
          />
        );
      })}
    </>
  );
}
